/**
 * Registry for external module reset handlers during store code changes.
 *
 * module-quote must NOT depend on module-checkout or module-flash-sale.
 * Instead, those modules register their own reset callbacks here at boot time.
 *
 * Usage (in the host app or each module's provider):
 * ```typescript
 * import { storeChangeResetRegistry } from '@kidslabs/module-quote';
 * import { checkoutStoreAPI } from '@kidslabs/module-checkout';
 *
 * storeChangeResetRegistry.register('checkout', () => {
 *     checkoutStoreAPI.actions.resetCheckoutStore();
 * });
 * ```
 */

type ResetHandler = () => void | Promise<void>;

const handlers = new Map<string, ResetHandler>();

export const storeChangeResetRegistry = {
    /**
     * Register a reset handler for store code changes.
     * @param moduleId - Unique identifier for the module (e.g., 'checkout', 'flash-sale')
     * @param handler - Callback to reset the module's stores
     */
    register(moduleId: string, handler: ResetHandler): void {
        handlers.set(moduleId, handler);
    },

    /**
     * Unregister a previously registered handler.
     */
    unregister(moduleId: string): void {
        handlers.delete(moduleId);
    },

    /**
     * Execute all registered reset handlers.
     * Errors in individual handlers are caught and logged so one failure
     * does not prevent other modules from resetting.
     */
    async executeAll(): Promise<void> {
        const entries = Array.from(handlers.entries());
        await Promise.allSettled(
            entries.map(async ([moduleId, handler]) => {
                try {
                    await handler();
                } catch (error) {
                    console.error(`[StoreChangeResetRegistry] Error resetting module "${moduleId}":`, error);
                }
            })
        );
    },
} as const;
